import React from 'react';
import { BlogPost } from '../types';

export const BLOG_POSTS: BlogPost[] = [
  {
    id: 'talk-about-money',
    title: "How to Talk to Your Partner About Money Without Fighting",
    excerpt: "Financial discussions are the #1 cause of relationship stress. Learn the 3-step framework to have calm, productive conversations about finances.",
    date: "March 15, 2024",
    readTime: "5 min read",
    category: "Communication",
    imageUrl: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">Money is often cited as the leading cause of divorce and relationship stress. It represents safety, freedom, control, and values—all deeply personal concepts. When you talk about money, you aren't just talking about numbers; you're talking about your dreams and fears.</p>
      
      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">1. The "No-Ambush" Rule</h3>
      <p class="mb-4">Never bring up a budget deficit or a credit card bill when your partner has just walked in the door, is hungry, or is trying to relax. This puts them in a defensive "fight or flight" mode immediately.</p>
      <p class="mb-4"><strong>The Fix:</strong> Schedule it. "Hey, I'd love to spend 20 minutes on Sunday morning looking at our savings goals. Does that work for you?" This allows both parties to mentally prepare.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">2. Focus on Goals, Not Habits</h3>
      <p class="mb-4">Starting a conversation with "You spend too much on coffee" is an attack. It invites a counter-attack.</p>
      <p class="mb-4"><strong>The Fix:</strong> Frame the conversation around shared dreams. "I really want us to be able to afford that trip to Italy next year. To do that, I think we need to find $200 a month to save. Do you have ideas on where we could cut back?"</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">3. Practice with AI</h3>
      <p class="mb-4">If you are terrified of this conversation, practice it first. Use the <strong>LoveOnRead</strong> simulator to roleplay a "Financial Disagreement" scenario. It will help you refine your phrasing so you don't sound accusatory.</p>
    `
  },
  {
    id: 'keep-text-conversation-going',
    title: "7 Psychological Tricks to Keep a Text Conversation Going",
    excerpt: "Dying conversation? Don't let the spark fade. Use these proven techniques to turn dry texts into deep, engaging banter.",
    date: "March 22, 2024",
    readTime: "4 min read",
    category: "Texting",
    imageUrl: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">We've all been there. You send a message, and you get back... "lol" or "yeah." The conversation hits a wall. Before you send a double-text out of desperation, try these psychological triggers to revive the flow.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">1. The "Callback" Technique</h3>
      <p class="mb-4">Stand-up comedians use this constantly. Reference a joke or a topic from three days ago. "I just saw a golden retriever and thought of you—did you ever convince your landlord to let you get one?" It shows you listen and creates an inside joke.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">2. Assumption vs. Interrogation</h3>
      <p class="mb-4">Stop asking boring interview questions like "What do you do?" or "How are you?" Instead, make an assumption.</p>
      <p class="mb-4"><strong>Boring:</strong> "What is your job?"<br/><strong>Fun:</strong> "You have serious creative energy. I bet you're a graphic designer or an architect."</p>
      <p class="mb-4">If you're right, they are impressed. If you're wrong, they will eagerly correct you ("No way! I'm actually an accountant, why did you think that?"). Either way, you're talking.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">3. Use "Cliffhanger" Statements</h3>
      <p class="mb-4">"You are never going to guess who I just ran into..." or "I have a confession to make about our date last night."</p>
      <p class="mb-4">Curiosity is a powerful motivator. They <em>have</em> to reply to find out the answer.</p>

      <div class="bg-indigo-50 p-4 rounded-xl border-l-4 border-indigo-500 mb-6 mt-6">
        <p class="font-bold text-indigo-900 mb-1">Pro Tip:</p>
        <p class="text-indigo-800 text-sm">Use the LoveOnRead app to practice these techniques. Set the scenario to "Boring Responder" difficulty level and try to engage the AI. It's the perfect training ground.</p>
      </div>
    `
  },
  {
    id: 'anxious-attachment-texting',
    title: "Anxious Attachment? How to Stop Panic-Texting When They Don't Reply",
    excerpt: "If being left on 'read' sends you into a spiral, you might have an anxious attachment style. Here is how to self-soothe and avoid sabotaging the relationship.",
    date: "March 21, 2024",
    readTime: "6 min read",
    category: "Mental Health",
    imageUrl: "https://images.unsplash.com/photo-1614743260824-7836d5e1ba28?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">It's been 3 hours. You know they are online because you saw them post a story. But your text remains unanswered. Your chest tightens. <em>"Are they mad? Did I say something wrong? Are they breaking up with me?"</em></p>
      <p class="mb-4">Welcome to the world of Anxious Attachment. The good news? You aren't crazy, your brain is just wired to perceive distance as danger.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">The "Protest Behavior" Trap</h3>
      <p class="mb-4">When anxiety hits, we often engage in protest behaviors to force a reaction:</p>
      <ul class="list-disc pl-5 mb-4 space-y-2 text-slate-700">
        <li><strong>Double/Triple Texting:</strong> Trying to get their attention.</li>
        <li><strong>The "Whatever" Text:</strong> Acting cold or passive-aggressive.</li>
        <li><strong>Blocking/Deleting:</strong> Trying to regain control by cutting them off prematurely.</li>
      </ul>
      <p class="mb-4">These behaviors usually push the other person away—creating the exact abandonment you fear.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">The 20-Minute Rule</h3>
      <p class="mb-4">When you feel the urge to send a panic text, set a timer for 20 minutes. Put your phone in another room. Do something physical—do dishes, jumping jacks, or pet your dog.</p>
      <p class="mb-4">Physiological arousal (anxiety) usually peaks and then drops after 20 minutes. Re-read your draft text after the timer. 9 times out of 10, you will delete it.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">Rewrite the Narrative</h3>
      <p class="mb-4">Your brain tells you: "They aren't replying because they hate me."<br/>Force yourself to offer 3 alternatives:</p>
      <ol class="list-decimal pl-5 space-y-2 text-slate-700 mb-6">
        <li>They fell asleep.</li>
        <li>They are driving.</li>
        <li>They read it, thought "I'll reply in a sec," and got distracted by a work email.</li>
      </ol>
    `
  },
  {
    id: 'first-date-questions-chemistry',
    title: "First Date Questions That Actually Build Chemistry (No Small Talk)",
    excerpt: "Stop asking 'Where are you from?' and start asking questions that spark attraction. A guide to skipping the boring small talk.",
    date: "March 18, 2024",
    readTime: "5 min read",
    category: "Dating",
    imageUrl: "https://images.unsplash.com/photo-1511988617509-a57c8a288659?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">A first date is not a job interview. Yet, most of us treat it like one. "Where did you go to school?" "How many siblings do you have?" "Do you like your job?"</p>
      <p class="mb-4">These questions elicit facts, not feelings. <strong>Attraction is based on feelings.</strong> To build chemistry, you need to ask questions that reveal values, passions, and stories.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">1. The Passion Check</h3>
      <p class="mb-4"><em>"What is something you could talk about for 30 minutes straight with zero preparation?"</em></p>
      <p class="mb-4">This question lights people up. Whether it's Star Wars, pottery, or conspiracy theories, you get to see their eyes shine. That energy is attractive.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">2. The "Reality" Check</h3>
      <p class="mb-4"><em>"What’s the most spontaneous thing you’ve done recently?"</em></p>
      <p class="mb-4">This tells you about their lifestyle. Are they rigid planners? Or are they open to adventure? It also invites a fun story rather than a resume bullet point.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">3. The Vulnerability Opener</h3>
      <p class="mb-4"><em>"What’s a seemingly small thing that makes you really happy?"</em></p>
      <p class="mb-4">Maybe it's the smell of old books or the first sip of coffee. This builds intimacy without being too heavy for a first date.</p>

      <hr class="my-6 border-slate-200" />
      <p class="mb-4 italic text-slate-500">Practice these lines in our "First Date" scenario mode to see how an AI persona reacts before you try them in real life!</p>
    `
  },
  {
    id: 'long-distance-tips',
    title: "Navigating Long Distance: Beyond 'Good Morning' Texts",
    excerpt: "Distance makes the heart grow fonder, but it can also create anxiety. Here are actionable tips to keep the spark alive when you are miles apart.",
    date: "March 10, 2024",
    readTime: "4 min read",
    category: "LDR",
    imageUrl: "https://msgvibes.com/wp-content/uploads/2026/01/long-distance-good-morning-messages-1-1248x702.webp",
    content: `
      <p class="mb-4">Long-distance relationships (LDRs) require a completely different communication skillset than local relationships. Without physical touch and shared activities, your words become your entire connection.</p>
      
      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">The "Boring" Details Matter</h3>
      <p class="mb-4">When you only talk once a day, you tend to stick to the highlights: "Work was good," "I went to the gym." This creates a highlight reel, not intimacy.</p>
      <p class="mb-4"><strong>Try this:</strong> Share the mundane sensory details. "I had this awful cold brew coffee today that tasted like burnt toast." "I saw a dog that looked exactly like Snoopy." These small details make your partner feel like they are <em>living</em> your day with you.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">Create Shared Experiences</h3>
      <p class="mb-4">Don't just stare at each other on FaceTime. Do things "together" apart.</p>
      <ul class="list-disc pl-5 mb-4 space-y-2">
        <li>Watch a movie simultaneously using Teleparty.</li>
        <li>Cook the same recipe while on video call.</li>
        <li>Read the same book chapter and discuss it.</li>
      </ul>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">Anxiety Management</h3>
      <p class="mb-4">Silence is louder in an LDR. If you're going to be busy for 6 hours, just say so. "Heads up, super busy work day, won't be able to text until 6 PM." This prevents your partner from spiraling into "Are they mad at me?" thoughts.</p>
    `
  },
  {
    id: 'dating-red-flags-vs-icks',
    title: "The 'Ick' vs. Red Flags: When to Trust Your Gut",
    excerpt: "Is it a warning sign or are you just being picky? How to distinguish between dangerous red flags and the harmless 'ick'.",
    date: "March 08, 2024",
    readTime: "5 min read",
    category: "Dating Advice",
    imageUrl: "https://images.unsplash.com/photo-1517849845537-4d257902454a?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">The "ick" has taken over social media. It's that sudden shudder of repulsion you get when a date does something cringe-worthy—like chasing a ping pong ball or using a weird font on their phone.</p>
      <p class="mb-4">But conflating "icks" with "red flags" is dangerous. One is superficial; the other is about safety and compatibility.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">What is an "Ick"?</h3>
      <p class="mb-4">An ick is subjective and usually harmless. It often stems from seeing someone in a vulnerable or ungainly moment. 
      <br/><em>Examples: Clapping when the plane lands, wearing socks with sandals, pronouncing a word wrong.</em></p>
      <p class="mb-4"><strong>The Verdict:</strong> Push through it. If you really like them, the ick usually fades.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">What is a Red Flag?</h3>
      <p class="mb-4">A red flag is an objective indicator of future behavior, character issues, or lack of respect.</p>
      <ul class="list-disc pl-5 mb-4 space-y-2 text-slate-700">
        <li><strong>The "Crazy Ex" Narrative:</strong> If all their exes are "crazy," they are the common denominator.</li>
        <li><strong>Rude to Waiters:</strong> The #1 indicator of how they will eventually treat you.</li>
        <li><strong>Pushing Boundaries:</strong> If you say "no" to a drink or a touch and they pressure you, run.</li>
      </ul>
      <p class="mb-4"><strong>The Verdict:</strong> Do not ignore these. They do not get better with time; they get worse.</p>
    `
  },
  {
    id: 'art-of-apology',
    title: "Why 'I'm Sorry' Isn't Enough: The Art of the Apology",
    excerpt: "A bad apology can make a fight worse. Learn the 4 parts of a genuine apology that actually heals emotional wounds.",
    date: "March 05, 2024",
    readTime: "6 min read",
    category: "Conflict Resolution",
    imageUrl: "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">We've all heard the non-apology: "I'm sorry you feel that way." This is technically gaslighting—it shifts the blame from your actions to their reaction. A real apology requires vulnerability.</p>
      
      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">The 4 Parts of a Real Apology</h3>
      
      <div class="bg-indigo-50 p-4 rounded-xl border-l-4 border-indigo-500 mb-6">
        <ol class="list-decimal pl-5 space-y-2 font-medium text-slate-700">
          <li><strong>Express Remorse:</strong> "I am sorry."</li>
          <li><strong>Admit Responsibility:</strong> "I raised my voice and that was disrespectful." (Be specific!)</li>
          <li><strong>Make Amends:</strong> "I'm going to take a walk next time I feel that angry so I don't snap at you."</li>
          <li><strong>Ask for Forgiveness:</strong> "Can we move past this?"</li>
        </ol>
      </div>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">Validate Their Reality</h3>
      <p class="mb-4">Even if you didn't <em>mean</em> to hurt them, you must acknowledge that you <em>did</em>. Impact matters more than intent in the heat of the moment.</p>
      <p class="mb-4">Instead of "I didn't mean to ignore you," try "I see how my silence made you feel unimportant, and I am sorry for that."</p>
      
      <p class="mb-4">Practicing apologies is hard because our ego gets in the way. It's one of the most popular scenarios on <strong>LoveOnRead</strong> because it's a safe place to practice swallowing your pride.</p>
    `
  },
  {
    id: 'situationship-talk',
    title: "The 'What Are We?' Talk: How to Define the Relationship Without Being 'Needy'",
    excerpt: "Tired of the situationship limbo? Here is how to have the DTR (Define The Relationship) conversation with confidence and clarity.",
    date: "April 02, 2024",
    readTime: "5 min read",
    category: "Dating",
    imageUrl: "https://images.unsplash.com/photo-1516575972798-fa9526025230?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">The "situationship" is the modern dating scourge. You hang out, you hook up, you act like a couple... but there's no label. You're terrified to bring it up because you don't want to seem "chill" or "needy".</p>
      <p class="mb-4">Here is the truth: <strong>Clarity is attractive. Confusion is not.</strong> Asking for what you want isn't needy; it's a sign of high self-worth.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">1. Don't Ask, State</h3>
      <p class="mb-4">Instead of asking "What are we?", state your intentions. This takes the pressure off them to define it and puts you in the driver's seat.</p>
      <p class="mb-4"><strong>Try this:</strong> "I've really enjoyed getting to know you over the last few months. I'm at a point where I'm not looking to date other people, and I'd like to focus on us. How do you feel about that?"</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">2. Prepare for the "I'm not ready"</h3>
      <p class="mb-4">If they say they aren't ready for a label, believe them. Do not try to convince them. Do not wait around hoping they will change.</p>
      <p class="mb-4"><strong>The Response:</strong> "I understand. It sounds like we are looking for different things right now. I need to take a step back then, because I'm looking for a partner."</p>
      <p class="mb-4">This is the hardest part, but walking away is the ultimate power move. Often, it's the only thing that makes them realize your value.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">3. Practice the Scenario</h3>
      <p class="mb-4">This conversation is nerve-wracking. Use the <strong>LoveOnRead</strong> app to simulate a "DTR" conversation with our AI. Practice stating your needs without apologizing for them.</p>
    `
  },
  {
    id: 'love-bombing-vs-real',
    title: "Love Bombing vs. The Honeymoon Phase: How to Tell the Difference",
    excerpt: "Is it true love or manipulation? Learn to spot the signs of love bombing before you get too attached.",
    date: "April 05, 2024",
    readTime: "6 min read",
    category: "Red Flags",
    imageUrl: "https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">They text you 24/7. They say "I love you" after two weeks. They plan vacations for next year on the third date. It feels amazing... but is it real?</p>
      <p class="mb-4">"Love bombing" is a manipulation tactic used by narcissists to hook you quickly before their mask slips. Here is how to distinguish it from genuine interest.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">Sign #1: Pace vs. Intensity</h3>
      <p class="mb-4"><strong>Real Love:</strong> Builds over time. Intimacy is earned through shared experiences and vulnerability.</p>
      <p class="mb-4"><strong>Love Bombing:</strong> Instant intensity. They claim you are "soulmates" without actually knowing you. It feels rushed, like they are trying to lock you down.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">Sign #2: Boundaries</h3>
      <p class="mb-4"><strong>Real Love:</strong> Respects your time and boundaries. If you say you're busy, they say "Have fun!"</p>
      <p class="mb-4"><strong>Love Bombing:</strong> Guilt trips you for not spending every second with them. "I just miss you so much, don't you miss me?"</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">Sign #3: The Devalue Phase</h3>
      <p class="mb-4">Love bombing is always followed by devaluation. The moment you disappoint them or set a boundary, the affection turns off like a switch. If their love feels conditional on your compliance, it's not love—it's control.</p>
    `
  },
  {
    id: 'how-to-flirt-text',
    title: "How to Flirt Over Text Without Being Cringe (The Ultimate Guide)",
    excerpt: "Flirting is an art form. Learn how to be playful, tease, and build tension without crossing the line into awkward territory.",
    date: "April 08, 2024",
    readTime: "4 min read",
    category: "Texting",
    imageUrl: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=1000",
    content: `
      <p class="mb-4">Texting removes body language and tone, which makes flirting risky. One wrong emoji and you go from "sexy" to "creepy". Here are the rules of digital flirting.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">1. Push-Pull Dynamics</h3>
      <p class="mb-4">Flirting needs tension. If you are 100% complimentary ("You are so beautiful"), it can get boring. Add a playful challenge.</p>
      <p class="mb-4"><strong>Try this:</strong> "You look trouble in that picture." or "I'm not sure if we'd get along, you have terrible taste in pizza." (Followed by a smirk emoji 😉).</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">2. The "Less is More" Rule</h3>
      <p class="mb-4">Don't write paragraphs. Flirting should be like a game of ping pong—short, snappy volleys. If you send three bubbles for their one, you kill the mystery.</p>

      <h3 class="text-xl font-bold mt-6 mb-3 text-slate-800">3. Sensory Language</h3>
      <p class="mb-4">Instead of "I wish you were here," paint a picture.</p>
      <p class="mb-4">"This wine is amazing, but I suspect it would taste better if we were sharing it on my balcony right now."</p>
      
      <div class="bg-indigo-50 p-4 rounded-xl border-l-4 border-indigo-500 mb-6 mt-6">
        <p class="font-bold text-indigo-900 mb-1">Safety First:</p>
        <p class="text-indigo-800 text-sm">Unsure if a risky text will land? Test it on the LoveOnRead AI first. It will give you a "Cringe Score" and tell you if you're coming on too strong!</p>
      </div>
    `
  }
];